import { useMemo } from 'react';
import Select, {
  type StylesConfig,
  type SingleValue,
  type FilterOptionOption,
} from 'react-select';
import type { SectorTaxonomyNode } from '../../api/sectorTaxonomyApi';

export interface CanonicalSelectProps {
  /** canonical_sector_id */
  value: string;
  /** 선택값 변경. 빈 문자열로 클리어. */
  onChange: (canonicalSectorId: string) => void;
  /** 호출자(useTaxonomyList) 가 전달하는 활성 목록. */
  options: SectorTaxonomyNode[];
  /** sec_l1_meta_* prefix 자동 제외. default: true */
  excludeMetaL1?: boolean;
  placeholder?: string;
  disabled?: boolean;
  /** 접근성 — label 연결이 어려운 경우 보조 라벨. */
  ariaLabel?: string;
  /** label 의 htmlFor 와 연결할 input id. */
  inputId?: string;
}

interface Option {
  value: string;
  label: string;
  /** 검색 매칭용 raw 필드. label 에는 이미 둘 다 포함되지만,
   *  filterOption 가 사용자의 의도에 따라 명시적으로 두 필드를 본다. */
  displayName: string;
}

/**
 * 화면 11 정규 섹터(canonical) 선택용 공용 검색 select.
 *
 * - 입력어 → display_name + canonical_sector_id 부분 매칭 (대소문자 무시).
 * - 표시 형식: `display_name (canonical_sector_id)`
 * - 메타 L1 (sec_l1_meta_*) 자동 제외 (excludeMetaL1 default true).
 * - 키보드 navigation: ↑↓ + Enter (react-select 기본).
 *
 * pca-* 일반 select 와 시각적 정합을 맞추기 위해 styles prop 로 토큰 매핑.
 * (height 32px / border 1px solid --seal-line-bold / focus ring --seal-focus 등)
 */
export function CanonicalSelect({
  value,
  onChange,
  options,
  excludeMetaL1 = true,
  placeholder = '— 선택 —',
  disabled,
  ariaLabel,
  inputId,
}: CanonicalSelectProps): JSX.Element {
  const items: Option[] = useMemo(() => {
    const filtered = excludeMetaL1
      ? options.filter((o) => !o.canonicalSectorId.startsWith('sec_l1_meta_'))
      : options;
    return filtered.map((o) => ({
      value: o.canonicalSectorId,
      label: `${o.displayName} (${o.canonicalSectorId})`,
      displayName: o.displayName,
    }));
  }, [options, excludeMetaL1]);

  const selected = useMemo(
    () => items.find((it) => it.value === value) ?? null,
    [items, value]
  );

  function handleChange(opt: SingleValue<Option>): void {
    onChange(opt?.value ?? '');
  }

  // display_name + canonical_sector_id 양쪽 부분 매칭 (대소문자 무시).
  function filterOption(option: FilterOptionOption<Option>, raw: string): boolean {
    const q = raw.trim().toLowerCase();
    if (q.length === 0) return true;
    const dn = option.data.displayName.toLowerCase();
    const id = option.data.value.toLowerCase();
    return dn.includes(q) || id.includes(q);
  }

  return (
    <Select<Option>
      inputId={inputId}
      aria-label={ariaLabel}
      classNamePrefix="canonical-select"
      options={items}
      value={selected}
      onChange={handleChange}
      isDisabled={disabled}
      isClearable
      placeholder={placeholder}
      filterOption={filterOption}
      noOptionsMessage={() => '일치하는 정규 섹터 없음'}
      styles={styles}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
      menuPosition="fixed"
    />
  );
}

/** pca-input / pca-select 와 시각적 정합 — 토큰(var(--...)) 직접 참조. */
const styles: StylesConfig<Option, false> = {
  control: (base, state) => ({
    ...base,
    minHeight: 32,
    height: 32,
    borderRadius: 'var(--radius-sm)',
    borderColor: state.isFocused
      ? 'var(--accent)'
      : 'var(--seal-line-bold)',
    backgroundColor: 'var(--inset)',
    boxShadow: state.isFocused ? '0 0 0 3px var(--seal-focus)' : 'none',
    fontSize: 'var(--text-sm)',
    '&:hover': {
      borderColor: state.isFocused
        ? 'var(--accent)'
        : 'var(--seal-line-bold)',
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 var(--space-2)',
    height: 30,
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    color: 'var(--ink)',
  }),
  singleValue: (base) => ({
    ...base,
    color: 'var(--ink)',
  }),
  placeholder: (base) => ({
    ...base,
    color: 'var(--ink-faint)',
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 30,
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  menu: (base) => ({
    ...base,
    backgroundColor: 'var(--surface)',
    border: '1px solid var(--seal-line)',
    borderRadius: 'var(--radius-sm)',
    zIndex: 50,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 1000,
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 'var(--text-sm)',
    color: state.isSelected ? 'var(--accent-ink)' : 'var(--ink)',
    backgroundColor: state.isSelected
      ? 'var(--accent-soft)'
      : state.isFocused
      ? 'var(--seal-hover, var(--accent-soft))'
      : 'transparent',
    cursor: 'pointer',
  }),
};
